/** Désignation de l'action attendue (R12, « UX/UI parfaite » d'Alexis : action bloquée → action attendue
 *  désignée). Quand un préalable manque, le contrôle bloqué (`blocked` + `onBlocked`) appelle `designate()` : la
 *  vue concernée s'affiche (à la charge de l'appelant : `setView`, `requestSave`), la cible vient à l'écran, clignote
 *  trois fois en bleu d'action et pulse (`.wayfinding`) quelques secondes, un champ est mis au point ; l'appelant
 *  affiche ensuite un avis court (`notify`). Un `title` n'explique jamais seul un blocage : il est invisible au
 *  doigt.
 *
 *  Cibles : attribut `data-attention="<nom>"` sur le contrôle, retrouvé par `attentionSelector(nom)`. Noms
 *  réservés : `dossier-create` (créer le dossier, accueil), `dossier-name` (nom du dossier, Données initiales),
 *  `briefing-write` (rédiger la mise en ambiance), `country-write` (rédiger la fiche pays), `order-generate` (générer
 *  l'OPO).
 *
 *  Clignotement : animation Web au-dessus des animations CSS (comme la pulsation `wayfinding`), relançable à chaque
 *  demande, sans démonter l'élément. Mouvement réduit : un halo fixe, défilement sans animation.
 *
 *  Manière (`AttentionKind`) : `auto` (défaut) choisit `field` pour un champ, `button` pour un bouton principal
 *  (`data-variant="primary"`, posé par `Button`, `ButtonLink` et `IconButton`), `ring` pour tout le reste (bouton
 *  secondaire, lien, `ChoiceCard`, ligne) — un fond qui passerait au bleu rendrait leur texte illisible. */

import { tokenColor, tokenRgb } from "./theme";

/** Noms de cible réservés (`data-attention`). */
export type AttentionName =
  | "dossier-create"
  | "dossier-name"
  | "briefing-write"
  | "country-write"
  | "order-generate"
  | (string & {});

/** Cible du bouton « À régénérer » (ou « À revoir ») d'une rubrique de la fiche pays ou de la mise en ambiance : le
 *  menu d'export du dossier désigne la première de la vue ouverte quand un document ne suit plus le dossier. */
export const STALE_REGENERATE = "stale-regenerate";

/** Sélecteur d'une cible nommée : `[data-attention="dossier-name"]`. */
export function attentionSelector(name: AttentionName): string {
  return `[data-attention="${name}"]`;
}

/** Durée de la pulsation posée par `designate` (trois cycles de 1,6 s). */
export const DESIGNATE_PULSE_MS = 4800;

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Manière de désigner : `button` — bouton principal (fond noir) qui passe au bleu d'action ; `field` — champ cerné
 *  de bleu ; `ring` — halo bleu seul, sans toucher aux couleurs de la cible ; `auto` — selon la cible. */
export type AttentionKind = "auto" | "button" | "field" | "ring";

/** Manière effective pour une cible (`auto` résolu). */
export function attentionKindFor(element: HTMLElement, kind: AttentionKind = "auto"): Exclude<AttentionKind, "auto"> {
  if (kind !== "auto") return kind;
  if (isField(element)) return "field";
  return element.getAttribute?.("data-variant") === "primary" ? "button" : "ring";
}

/** Clignotement seul (sans défilement ni pulsation) : voir `AttentionKind`. */
export function flashAttention(element: HTMLElement | null, kind: AttentionKind = "auto"): void {
  if (!element || typeof element.animate !== "function") return;
  const accentRgb = tokenRgb("accent");
  const accent = tokenColor("accent");
  const ink = tokenColor("ink");
  const ring = (spread: number, alpha: number) => `0 0 0 ${spread}px rgb(${accentRgb} / ${alpha})`;
  if (reducedMotion()) {
    element.animate([{ boxShadow: ring(3, 0.45) }, { boxShadow: ring(3, 0.45) }], { duration: 2400 });
    return;
  }
  const resolved = attentionKindFor(element, kind);
  const keyframes: Keyframe[] =
    resolved === "button"
      ? [
          { boxShadow: ring(0, 0.55), backgroundColor: ink },
          { boxShadow: ring(7, 0), backgroundColor: accent, offset: 0.45 },
          { boxShadow: ring(0, 0), backgroundColor: ink },
        ]
      : resolved === "field"
        ? [
            { boxShadow: ring(0, 0.5), borderColor: accent },
            { boxShadow: ring(6, 0), borderColor: accent, offset: 0.45 },
            { boxShadow: ring(0, 0), borderColor: accent },
          ]
        : [
            { boxShadow: ring(0, 0.55) },
            { boxShadow: ring(6, 0), offset: 0.45 },
            { boxShadow: ring(0, 0) },
          ];
  element.animate(keyframes, { duration: 800, iterations: 3, easing: "ease-in-out" });
}

/** Pulsations posées par `designate` (et leur minuterie) : une nouvelle désignation prolonge la sienne ; une
 *  classe `wayfinding` posée par l'application elle-même n'est jamais retirée. */
const pulses = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

/** Ancêtre en cours de sortie : vue ou panneau encore monté le temps de son animation de sortie (`Presence`). */
const LEAVING = '[class*="gx-exit-"], .gx-drawer-out, [data-closing]';

function isLeaving(element: HTMLElement): boolean {
  try {
    return typeof element.closest === "function" && element.closest(LEAVING) !== null;
  } catch {
    return false;
  }
}

/** Cible d'un sélecteur : la première qui n'appartient pas à une vue en train de sortir (deux vues portant le même
 *  `data-attention` coexistent pendant la transition). */
function resolve(target: HTMLElement | string | null | undefined): HTMLElement | null {
  if (!target) return null;
  if (typeof target !== "string") return target;
  try {
    if (typeof document === "undefined") return null;
    for (const element of document.querySelectorAll<HTMLElement>(target)) if (!isLeaving(element)) return element;
    return null;
  } catch {
    return null;
  }
}

/** Cible entièrement visible dans la fenêtre et dans son plus proche ancêtre qui défile. Sans mise en page
 *  (essais), la cible est tenue pour hors de vue : le défilement est demandé comme avant. */
function fullyVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect?.();
  if (!rect || (rect.width === 0 && rect.height === 0)) return false;
  if (rect.top < 0 || rect.bottom > window.innerHeight) return false;
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const overflow = getComputedStyle(parent).overflowY;
    if (overflow !== "auto" && overflow !== "scroll") continue;
    const box = parent.getBoundingClientRect();
    return rect.top >= box.top && rect.bottom <= box.bottom;
  }
  return true;
}

function isField(element: HTMLElement): boolean {
  const tag = element.tagName?.toUpperCase?.() ?? "";
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || element.isContentEditable === true;
}

/** Événement émis (bouillonnant, annulable) par `designate` sur une cible cachée dans un dépliage fermé
 *  (`[data-open="false"]`, `Disclosure`) : le volet qui l'écoute s'ouvre et appelle `preventDefault()` ; la
 *  désignation reprend alors après l'ouverture (`REVEAL_MS`). Sans écouteur, la cible est désignée telle quelle. */
export const REVEAL_EVENT = "gx-reveal";
/** Attente de l'ouverture d'un dépliage (`--gx-enter`, 180 ms) et de l'animation de hauteur du panneau qui le
 *  contient (260 ms, `useAnimatedHeight`) : le défilement vers la cible part d'une mise en page arrêtée. */
export const REVEAL_MS = 300;
const revealing = new WeakSet<HTMLElement>();

/** Demande au volet replié de s'ouvrir ; vrai s'il l'accepte (`preventDefault`). */
function askReveal(element: HTMLElement): boolean {
  if (typeof element.dispatchEvent !== "function" || typeof CustomEvent === "undefined") return false;
  return !element.dispatchEvent(new CustomEvent(REVEAL_EVENT, { bubbles: true, cancelable: true }));
}

function folded(element: HTMLElement): boolean {
  try {
    return typeof element.closest === "function" && element.closest('[data-open="false"]') !== null;
  } catch {
    return false;
  }
}

/** Désigne l'action attendue : la cible (élément ou sélecteur) vient à l'écran, est mise au point si c'est un
 *  champ (`kind="field"` ou champ de saisie), clignote (`flashAttention`, manière `kind`) et pulse (`.wayfinding`) pendant
 *  4,8 s — sauf si elle pulsait déjà, auquel cas sa classe est laissée telle quelle. Rend la cible, ou null si elle
 *  est introuvable. */
export function designate(target: HTMLElement | string | null | undefined, kind: AttentionKind = "auto"): HTMLElement | null {
  const element = resolve(target);
  if (!element) return null;
  // Cible dans un dépliage fermé (étape repliée des Données initiales, options facultatives) : le volet qui la tient
  // est prié de s'ouvrir (`REVEAL_EVENT`) ; s'il l'accepte, la désignation suit son ouverture.
  if (!revealing.has(element) && folded(element) && askReveal(element)) {
    revealing.add(element);
    setTimeout(() => {
      designate(element, kind);
      revealing.delete(element);
    }, REVEAL_MS);
    return element;
  }
  const reduced = reducedMotion();
  // Cible déjà entière à l'écran : rien ne défile (un défilement doux déjà lancé par l'appelant n'est pas
  // interrompu) ; sinon elle vient au centre de son panneau, jamais au bord où une rangée de pied la cacherait.
  if (!fullyVisible(element)) {
    element.scrollIntoView?.({ block: "center", inline: "nearest", behavior: reduced ? "auto" : "smooth" });
  }
  if (kind === "field" || isField(element)) element.focus?.({ preventScroll: true });
  flashAttention(element, kind);

  const own = pulses.get(element);
  if (own !== undefined) clearTimeout(own);
  else if (element.classList.contains("wayfinding")) return element; // pulsation de l'application : intouchée
  element.classList.add("wayfinding");
  pulses.set(
    element,
    setTimeout(() => {
      pulses.delete(element);
      element.classList.remove("wayfinding");
    }, DESIGNATE_PULSE_MS),
  );
  return element;
}

/** Désigne une cible qui n'existe pas encore : après un changement de vue (`setView`), la vue se monte puis
 *  entre en glissé. Cherche la cible toutes les 50 ms pendant `delay` ms au plus, en écartant celle de la vue qui
 *  s'en va (ancêtre en sortie, `gx-exit-*`), puis la désigne. `onDone` reçoit la cible, ou null si elle n'est
 *  pas apparue. Rend de quoi annuler. */
export function designateSoon(
  selector: string,
  kind: AttentionKind = "auto",
  delay = 1200,
  onDone?: (element: HTMLElement | null) => void,
): () => void {
  const started = Date.now();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const tick = () => {
    timer = null;
    const element = resolve(selector);
    if (element) {
      // Désignée avant d'appeler `onDone` : `onDone?.(designate(…))` n'évaluait pas `designate` sans `onDone`
      // (appel facultatif court-circuité), et la cible trouvée n'était jamais désignée.
      const shown = designate(element, kind);
      onDone?.(shown);
      return;
    }
    if (Date.now() - started >= delay) {
      onDone?.(null);
      return;
    }
    timer = setTimeout(tick, 50);
  };
  timer = setTimeout(tick, 50);
  return () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
}
